import { ref, toValue, watch, type MaybeRefOrGetter } from "vue";
import type { ChatAgent, ChatAvailabilityResponse, ChatSurface } from "../types/chat";
import {
  normalizeAgentCompletionMode,
  normalizeAgentInputMode,
  normalizeAgentLayout,
} from "../lib/agent-format";

/**
 * Pre-flight check of whether chat can actually be started for the current
 * surface/context. Mirrors the server-side `StartChat` resolution (an agent
 * governs the context AND a usable provider credential is configured on the
 * agent file or in site settings). It also returns the safe public projection of
 * the exact agent the server resolved, so presentation and execution use the same
 * ancestry/fallback rules.
 *
 * There is no platform-default credential: when no key resolves the chat input
 * must be disabled with a visible clue rather than failing only after the user
 * types. The state defaults to **unavailable** until the fetch resolves so the
 * input never flickers enabled → disabled on load.
 *
 * Client-only (depends on the browser session token); SSR leaves it unresolved.
 */
export interface UseChatAvailabilityOptions {
  /** Resource path the chat is scoped to. Forwarded to the availability query. */
  contextPath?: MaybeRefOrGetter<string | undefined>;
}

export function useChatAvailability(
  surface: ChatSurface,
  siteId: MaybeRefOrGetter<string | undefined>,
  options: UseChatAvailabilityOptions = {},
) {
  const { apiUrl, authHeaders } = useChatHttp();

  // Default disabled: stay unavailable until the first resolve completes so the
  // input is never briefly enabled then disabled.
  const available = ref(false);
  const agent = ref<ChatAgent | null>(null);
  const loading = ref(true);

  // Race guard: a stale fetch must never overwrite a newer resolution.
  let seq = 0;

  async function resolve(): Promise<void> {
    if (!import.meta.client) {
      return;
    }

    const site = toValue(siteId);
    if (!site) {
      available.value = false;
      agent.value = null;
      loading.value = false;
      return;
    }

    const mine = ++seq;
    available.value = false;
    if (surface === "candidate") {
      agent.value = null;
    }
    loading.value = true;
    try {
      const path = toValue(options.contextPath);
      const query = path ? `?contextPath=${encodeURIComponent(path)}` : "";
      const result = await $fetch<ChatAvailabilityResponse>(
        `${apiUrl()}/v1/chat/${surface}/availability${query}`,
        { headers: authHeaders(site) },
      );
      if (mine !== seq) {
        return;
      }
      available.value = result?.available === true;
      const resolvedAgent = result?.agent;
      agent.value = resolvedAgent
        ? {
            ...resolvedAgent,
            initialMessage:
              typeof resolvedAgent.initialMessage === "string"
              && resolvedAgent.initialMessage.trim()
                ? resolvedAgent.initialMessage.trim()
                : undefined,
            input: normalizeAgentInputMode(resolvedAgent.input),
            completion: normalizeAgentCompletionMode(resolvedAgent.completion),
            layout: normalizeAgentLayout(resolvedAgent.layout),
          }
        : null;
    } catch {
      // Treat any failure (auth, not-configured, network) as unavailable.
      if (mine !== seq) {
        return;
      }
      available.value = false;
      agent.value = null;
    } finally {
      if (mine === seq) {
        loading.value = false;
      }
    }
  }

  watch(
    () => [toValue(siteId), toValue(options.contextPath)] as const,
    () => void resolve(),
    { immediate: true },
  );

  return { available, agent, loading, resolve };
}
